<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Drawer from '../../website/components/Drawer.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextToolbar from './TextToolbar.vue';
import RichTextEditor from './RichTextEditor.vue';
import DatePicker from './DatePicker.vue';
import FileUpload from './FileUpload.vue';

const props = defineProps({
  task: {
    type: Object,
    default: null,
  },
  show: {
    type: Boolean,
    required: true,
  },
  columns: {
    type: Object,
    required: true,
  },
  agents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'save', 'delete']);

const { t } = useI18n();

const loading = ref(false);
const richTextEditor = ref(null);
const newTag = ref('');
const toolbarActiveStates = ref({
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  link: false,
  heading: false,
  quote: false,
  highlight: false,
  color: false,
  alignLeft: true,
  alignCenter: false,
  alignRight: false,
});

// Formulaire éditable
const formData = ref({
  title: '',
  description: '',
  priority: 'medium',
  assignees: [], // Array d'IDs d'agents
  dueDate: '',
  status: 'backlog',
  files: [], // Array de fichiers
  url: '', // URL liée à la tâche
  tags: [], // Array de tags
});

// Charger les données de la tâche quand elle change
watch(
  () => props.task,
  newTask => {
    if (newTask) {
      formData.value = {
        title: newTask.title || '',
        description: newTask.description || '',
        priority: newTask.priority || 'medium',
        assignees: newTask.assignees || [],
        dueDate: newTask.dueDate || '',
        status: newTask.status || 'backlog',
        files: newTask.files || [],
        url: newTask.url || '',
        tags: newTask.tags || [],
      };
    } else {
      formData.value = {
        title: '',
        description: '',
        priority: 'medium',
        assignees: [],
        dueDate: '',
        status: 'backlog',
        files: [],
        url: '',
        tags: [],
      };
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.task?.id);
const modalTitle = computed(() =>
  isEdit.value ? t('TASKS.DRAWER.EDIT_TASK') : t('TASKS.DRAWER.NEW_TASK')
);

// Options d'agents pour le multi-select
const agentOptions = computed(() =>
  props.agents.map(agent => ({
    value: agent.id,
    label: agent.name,
    avatar: agent.thumbnail || agent.avatar_url,
    email: agent.email,
  }))
);

// Options de priorité
const priorityOptions = computed(() => [
  {
    value: 'low',
    label: t('TASKS.PRIORITY.LOW'),
    icon: 'i-lucide-chevron-down',
  },
  {
    value: 'medium',
    label: t('TASKS.PRIORITY.MEDIUM'),
    icon: 'i-lucide-chevron-up',
  },
  {
    value: 'high',
    label: t('TASKS.PRIORITY.HIGH'),
    icon: 'i-lucide-chevrons-up',
  },
]);

// Options de status (colonnes) - chaque colonne est un statut unique
const statusOptions = computed(() =>
  Object.entries(props.columns).map(([key, column]) => ({
    value: key,
    label: column.title,
  }))
);

// Sauvegarder les modifications
const handleSubmit = () => {
  if (!formData.value.title?.trim()) {
    useAlert(t('TASKS.VALIDATION.TITLE_REQUIRED'));
    return;
  }

  emit('save', {
    ...props.task,
    ...formData.value,
  });
};

const handleClose = () => {
  emit('close');
};

const handleDelete = () => {
  emit('delete', props.task);
};

// Ajouter un tag
const addTag = () => {
  const tag = newTag.value.trim();
  if (tag && !formData.value.tags.includes(tag)) {
    formData.value.tags.push(tag);
    newTag.value = '';
  }
};

// Mettre à jour l'état de la toolbar
const handleSelectionChange = states => {
  toolbarActiveStates.value = { ...toolbarActiveStates.value, ...states };
};

// Gérer le formatage de texte depuis la toolbar
const handleFormat = ({ button, value }) => {
  if (!richTextEditor.value) return;

  const commandMap = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'strikeThrough',
    link: 'createLink',
    heading: 'formatBlock',
    quote: 'formatBlock',
    highlight: 'backColor',
    color: 'foreColor',
  };

  const command = commandMap[button];
  if (!command) return;

  // Commandes spéciales avec valeurs
  if (button === 'heading') {
    richTextEditor.value.formatWithValue('formatBlock', 'H3');
  } else if (button === 'quote') {
    richTextEditor.value.formatWithValue('formatBlock', 'BLOCKQUOTE');
  } else if (button === 'link') {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Entrez l'URL:", 'https://');
    if (url) {
      richTextEditor.value.formatWithValue('createLink', url);
    }
  } else if (button === 'highlight') {
    richTextEditor.value.formatWithValue('backColor', '#fef08a');
  } else if (button === 'color') {
    // eslint-disable-next-line no-alert
    const color = window.prompt('Entrez la couleur (ex: #ff0000):', '#000000');
    if (color) {
      richTextEditor.value.formatWithValue('foreColor', color);
    }
  } else if (button === 'align') {
    // Alignement
    const alignMap = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight',
    };
    richTextEditor.value.format(alignMap[value] || 'justifyLeft');
  } else {
    // Commandes simples (bold, italic, underline, strikethrough)
    richTextEditor.value.format(command);
  }
};

// Agent qui a créé la tâche
const createdByAgent = computed(() => {
  if (!props.task?.createdBy) return null;
  return props.agents.find(a => a.id === props.task.createdBy);
});

// Initiales du créateur
const getCreatedByInitials = agent => {
  if (!agent?.name) return '';
  return agent.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const unknownUserInitials = '?';

// Formater la date de création
const formatCreatedDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <Drawer :show="show" size="md" @close="handleClose">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] dark:border-[#2A2A2C]"
    >
      <h2 class="text-lg font-semibold text-[#1D1D1F] dark:text-white">
        {{ modalTitle }}
      </h2>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] transition-colors"
        @click="handleClose"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div class="space-y-6">
        <!-- Titre -->
        <div>
          <label
            class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
            for="task-title"
          >
            {{ t('TASKS.TASK_MODAL.TITLE_LABEL') }}
          </label>
          <Input
            id="task-title"
            v-model="formData.title"
            :placeholder="t('TASKS.TASK_MODAL.TITLE_PLACEHOLDER')"
            required
          />
        </div>

        <!-- Description -->
        <div>
          <label
            class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
            for="task-description"
          >
            {{ t('TASKS.TASK_MODAL.DESCRIPTION_LABEL') }}
          </label>

          <!-- Toolbar de formatage -->
          <div class="mb-2">
            <TextToolbar
              :active-states="toolbarActiveStates"
              @format="handleFormat"
            />
          </div>

          <RichTextEditor
            id="task-description"
            ref="richTextEditor"
            v-model="formData.description"
            :placeholder="t('TASKS.TASK_MODAL.DESCRIPTION_PLACEHOLDER')"
            @selection-change="handleSelectionChange"
          />
        </div>

        <!-- Priorité et Status -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
              for="task-priority"
            >
              {{ t('TASKS.TASK_MODAL.PRIORITY_LABEL') }}
            </label>
            <SelectMenu
              id="task-priority"
              v-model="formData.priority"
              :options="priorityOptions"
              :placeholder="t('TASKS.TASK_MODAL.PRIORITY_LABEL')"
            />
          </div>

          <div>
            <label
              class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
              for="task-status"
            >
              {{ t('TASKS.TASK_MODAL.STATUS_LABEL') }}
            </label>
            <SelectMenu
              id="task-status"
              v-model="formData.status"
              :options="statusOptions"
              :placeholder="t('TASKS.TASK_MODAL.STATUS_LABEL')"
            />
          </div>
        </div>

        <!-- Assignés (multi-select) -->
        <div>
          <label
            class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
            for="task-assignees"
          >
            {{ t('TASKS.TASK_MODAL.ASSIGNEES_LABEL') }}
          </label>
          <TagMultiSelectComboBox
            id="task-assignees"
            v-model="formData.assignees"
            :options="agentOptions"
            :placeholder="t('TASKS.TASK_MODAL.ASSIGNEES_PLACEHOLDER')"
            :search-placeholder="t('TASKS.TASK_MODAL.SEARCH_AGENTS')"
            :empty-state="t('TASKS.TASK_MODAL.NO_AGENTS')"
          />
        </div>

        <!-- Date d'échéance -->
        <DatePicker
          v-model="formData.dueDate"
          :label="t('TASKS.TASK_MODAL.DUE_DATE_LABEL')"
          :placeholder="t('TASKS.TASK_MODAL.DUE_DATE_PLACEHOLDER')"
        />

        <!-- Tags -->
        <div>
          <label
            class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
          >
            {{ t('TASKS.TASK_MODAL.TAGS_LABEL') }}
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <!-- Tags existants -->
            <span
              v-for="(tag, index) in formData.tags"
              :key="index"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58] border border-[#3860F9]/20 dark:border-[#D3FD58]/20"
            >
              {{ tag }}
              <button
                type="button"
                class="hover:text-red-600 dark:hover:text-red-400 transition-colors"
                @click="formData.tags.splice(index, 1)"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </span>

            <!-- Input pour nouveau tag -->
            <div class="flex items-center gap-2 flex-1 min-w-[200px]">
              <Input
                v-model="newTag"
                type="text"
                :placeholder="t('TASKS.TASK_MODAL.TAGS_PLACEHOLDER')"
                class="flex-1"
                @keydown.enter.prevent="addTag"
              />
              <Button
                icon="i-lucide-plus"
                color="slate"
                size="sm"
                variant="ghost"
                :label="t('TASKS.TASK_MODAL.ADD_TAG')"
                @click="addTag"
              />
            </div>
          </div>
        </div>

        <!-- URL -->
        <div>
          <label
            class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
            for="task-url"
          >
            {{ t('TASKS.TASK_MODAL.URL_LABEL') }}
          </label>
          <Input
            id="task-url"
            v-model="formData.url"
            type="url"
            :placeholder="t('TASKS.TASK_MODAL.URL_PLACEHOLDER')"
          >
            <template #prefix>
              <span
                class="i-lucide-link size-4 text-[#6B7280] dark:text-[#9CA3AF]"
              />
            </template>
          </Input>
        </div>

        <!-- Fichiers joints -->
        <FileUpload
          v-model="formData.files"
          :label="t('TASKS.TASK_MODAL.FILES_LABEL')"
        />

        <!-- Created By (si édition) -->
        <div
          v-if="isEdit && task.createdBy"
          class="pt-4 border-t border-[#E5E7EB] dark:border-[#2A2A2C]"
        >
          <div
            class="flex items-center gap-2 text-sm font-semibold text-[#1D1D1F] dark:text-white mb-3"
          >
            <span
              class="i-lucide-user-plus size-4 text-[#6B7280] dark:text-[#9CA3AF]"
            />
            <span>{{ t('TASKS.DRAWER.CREATED_BY') }}</span>
          </div>
          <div class="flex items-center gap-3 pl-6">
            <div
              v-if="createdByAgent?.thumbnail || createdByAgent?.avatar_url"
              class="size-10 rounded-full overflow-hidden ring-2 ring-n-background shadow-sm"
            >
              <img
                :src="createdByAgent.thumbnail || createdByAgent.avatar_url"
                :alt="createdByAgent.name"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              v-else
              class="size-10 rounded-full bg-slate-300 text-slate-800 flex items-center justify-center text-sm font-bold ring-2 ring-n-background shadow-sm"
            >
              <template v-if="createdByAgent">
                {{ getCreatedByInitials(createdByAgent) }}
              </template>
              <template v-else>
                {{ unknownUserInitials }}
              </template>
            </div>
            <div>
              <p class="text-sm font-medium text-[#1D1D1F] dark:text-white">
                {{ createdByAgent?.name || t('TASKS.DRAWER.UNKNOWN') }}
              </p>
              <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
                {{ task.createdAt ? formatCreatedDate(task.createdAt) : '' }}
              </p>
            </div>
          </div>
        </div>

        <!-- ID (si édition) -->
        <div v-if="isEdit" class="pt-4 border-t border-n-weak">
          <div class="flex items-center justify-between text-sm">
            <span class="text-[#6B7280] dark:text-[#9CA3AF]">{{
              t('TASKS.DRAWER.TASK_ID')
            }}</span>
            <span
              class="font-mono text-xs text-[#6B7280] dark:text-[#9CA3AF] bg-[#F9FAFB] dark:bg-[#1D1D1F] px-2 py-1 rounded"
            >
              <span>{{ `#${task.id}` }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-between gap-3 px-6 py-4 border-t border-[#E5E7EB] dark:border-[#2A2A2C]"
    >
      <Button
        v-if="isEdit"
        :label="t('TASKS.DRAWER.DELETE')"
        icon="i-lucide-trash-2"
        variant="ghost"
        size="sm"
        color="slate"
        class="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
        @click="handleDelete"
      />
      <div v-else />
      <div class="flex items-center gap-3">
        <Button
          :label="t('TASKS.DRAWER.CANCEL')"
          variant="outline"
          color="slate"
          @click="handleClose"
        />
        <Button
          :label="isEdit ? t('TASKS.DRAWER.SAVE') : t('TASKS.DRAWER.CREATE')"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Drawer>
</template>
